import { Card, colors, Divider, Grid, H2, Row, Separator, Skeleton, useContainerWidth } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { GroupImage } from '@fs/zion-group-management'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useTreeGroupDetailsContext } from '../providers/TreeGroupDetailsProvider'
import getTreeUrl from '../../helpers/getTreeUrl'
import { useStartingPerson } from '../../providers/StartingPersonProvider'
import ViewResearcherTreeButton from '../../components/details/researcher-trees/ViewResearcherTreeButton'
import { useMembersContext } from '../../providers/MembersProvider'
import { useUserIsDataAdmin } from '../../providers/DataAdminProvider'
import AboutThisTreeContent from '../../components/details/researcher-trees/AboutThisTreeContent'
import { useGedcomData } from '../../providers/GedcomDataProviderSmart'
import StartingPerson, { StartingPersonSkeleton } from './StartingPerson'
import EditTreeDetails from './EditTreeDetails'
import PreferredTreeToggler, { PreferredTreeTogglerSkeleton } from './PreferredTreeToggler'
import DeleteTree from './DeleteTree'

export default function DetailsContent(): JSX.Element {
  const [t] = useTranslation()
  const { group, changeImageOverlay } = useTreeGroupDetailsContext()
  const { startingPersonId } = useStartingPerson()
  const { gedcomData } = useGedcomData()
  const atWidth = useContainerWidth()
  const { treeDetails } = useGroupsContext()
  const { userIsAdmin, currentMemberDetails } = useMembersContext()
  const { userIsDataAdmin } = useUserIsDataAdmin()
  const userIsOwner = !!treeDetails && currentMemberDetails?.memberCisId === treeDetails.ownerCisId
  const showMobileText = atWidth({ default: true, xxs: false })
  const viewTreeUrl = getTreeUrl({
    treeId: group.treeIds?.[0],
    pedigreeType: 'landscape',
    mePersonId: startingPersonId,
  })

  return (
    <Card backgroundColor={colors.gray00} edges="none">
      <Row>
        <H2 size="H5">{t('group-management-page.heading.tree-details')}</H2>
        <div growX />
        {/* eslint-disable-next-line test-selectors/button -- NA */}
        <ViewResearcherTreeButton showMobileText={showMobileText} viewTreeUrl={viewTreeUrl} />
      </Row>

      <Separator size="nano" />

      <Grid gutters="sm">
        {/* Row is needed to align GroupImage to the start */}
        <Row>
          <GroupImage
            group={group}
            // TODO should we should update GroupImage to allow handleClick so that we focus back on GroupImage after closing the overlay. We should also show indication when it is active.
            onGroupImageClick={() => changeImageOverlay.open()}
            isAdmin={userIsAdmin && !userIsDataAdmin}
            noShrinkX
          />
        </Row>

        <Card backgroundColor={colors.gray03} edges="none" margins="xs">
          <AboutThisTreeContent gedcomData={gedcomData} />
        </Card>

        {userIsAdmin && <EditTreeDetails />}

        <StartingPerson />

        <PreferredTreeToggler />

        {userIsOwner && (
          <>
            <Divider />
            <Separator size="sm" />
            <DeleteTree />
          </>
        )}
      </Grid>
    </Card>
  )
}

export function DetailsContentSkeleton(): JSX.Element {
  return (
    <Card backgroundColor={colors.gray00} edges="none">
      <Row>
        <Skeleton.H5 growX />
        <div growX />
        {/* eslint-disable-next-line test-selectors/button -- NA */}
        <Skeleton.Button />
      </Row>

      <Separator size="nano" />

      <Grid gutters="sm">
        <Row>
          <Skeleton.Image height={180} width={240} />
        </Row>

        <Card backgroundColor={colors.gray03} edges="none" margins="xs">
          <Skeleton.Paragraph maxWidth={300} />
          <div />
          <Skeleton.Paragraph maxWidth={300} />
        </Card>

        <StartingPersonSkeleton />

        <PreferredTreeTogglerSkeleton />
      </Grid>
    </Card>
  )
}
